import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { take } from 'rxjs';
import { TypeCesEnum } from 'src/app/domain/registration/enums/type-ces.enum';
import { DialogConfirmComponent } from 'src/app/shared/components/dialog-confirm/dialog-confirm.component';
import { GlobalUtils } from 'src/app/shared/global-utils';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { CustomerBranch } from '../customer-branch.util';
import { CustomerResponsibleDialogComponent } from '../customer-responsible-dialog/customer-responsible-dialog.component';
import { CustomerResponsibleService } from '../customer-responsible.service';
import {
  CustomerResponsible,
  filterResponsibles,
  removeResponsible,
  responsibleBranchLabel,
  responsibleCityLabel,
  toCustomerResponsible,
  upsertResponsible,
} from '../customer-responsible.util';

@Component({
  selector: 'app-customer-responsibles-panel',
  templateUrl: './customer-responsibles-panel.component.html',
  styleUrls: ['./customer-responsibles-panel.component.sass'],
})
export class CustomerResponsiblesPanelComponent implements OnChanges {
  @Input() customerId: string | null = null;
  @Input() typeCes: TypeCesEnum = TypeCesEnum.NEW;
  @Input() branches: CustomerBranch[] = [];
  @Input() drafts: CustomerResponsible[] = [];
  @Output() draftsChange = new EventEmitter<CustomerResponsible[]>();

  searchTerm = '';
  loading = false;
  saved: CustomerResponsible[] = [];
  readonly displayedColumns = ['name', 'branch', 'document', 'contact', 'city', 'actions'];

  constructor(
    private dialog: MatDialog,
    private responsibleService: CustomerResponsibleService,
    private toastService: ToastService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customerId'] && this.customerId && this.typeCes !== TypeCesEnum.NEW) {
      this.load();
    }
  }

  get readonly(): boolean {
    return this.typeCes === TypeCesEnum.SHOW;
  }

  get rows(): CustomerResponsible[] {
    const source = this.typeCes === TypeCesEnum.NEW ? this.drafts : this.saved;
    return filterResponsibles(source, this.searchTerm);
  }

  branchLabel(row: CustomerResponsible): string {
    return responsibleBranchLabel(row, this.branches);
  }

  cityLabel(row: CustomerResponsible): string {
    return responsibleCityLabel(row);
  }

  load(): void {
    if (!this.customerId) return;
    this.loading = true;
    this.responsibleService
      .list(this.customerId)
      .pipe(take(1))
      .subscribe({
        next: (rows) => {
          this.saved = (rows || []).map((row) => toCustomerResponsible(row));
          this.loading = false;
        },
        error: (error) => {
          this.saved = [];
          this.loading = false;
          this.notify(error, 'Não foi possível carregar os responsáveis deste cliente.');
        },
      });
  }

  open(row?: CustomerResponsible, viewMode = false): void {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      component: CustomerResponsibleDialogComponent,
      data: {
        responsible: row || null,
        branches: this.branches,
        viewMode: viewMode || this.readonly,
      },
      dialogConfig: { width: '960px', maxWidth: '95vw' },
      callBack: (result?: CustomerResponsible) => {
        if (!result || viewMode || this.readonly) return;
        this.persist(result);
      },
    });
  }

  remove(row: CustomerResponsible): void {
    if (this.readonly) return;
    const dialogRef = this.dialog.open(DialogConfirmComponent, {
      width: '400px',
      data: {
        title: 'Confirmação',
        content: `Deseja remover o responsável "${row.responsibleName}"?`,
        yesText: 'Sim, remover',
        noText: 'Cancelar',
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result?.result !== true) return;
      if (this.typeCes === TypeCesEnum.NEW || !row.id || !this.customerId) {
        this.draftsChange.emit(removeResponsible(this.drafts, row));
        return;
      }
      this.responsibleService
        .delete(this.customerId, row.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.saved = removeResponsible(this.saved, row);
            this.toastService.showSuccess('Responsável removido com sucesso.');
          },
          error: (error) => this.notify(error, 'Não foi possível remover o responsável.'),
        });
    });
  }

  private persist(responsible: CustomerResponsible): void {
    if (this.typeCes === TypeCesEnum.NEW || !this.customerId) {
      this.draftsChange.emit(upsertResponsible(this.drafts, responsible));
      this.toastService.showSuccess('Responsável preparado para salvar com o cliente.');
      return;
    }
    const request$ = responsible.id
      ? this.responsibleService.update(this.customerId, responsible.id, responsible)
      : this.responsibleService.create(this.customerId, responsible);
    request$.pipe(take(1)).subscribe({
      next: (saved) => {
        this.saved = upsertResponsible(this.saved, toCustomerResponsible(saved || responsible));
        this.toastService.showSuccess('Responsável salvo com sucesso.');
      },
      error: (error) => this.notify(error, 'Não foi possível salvar o responsável.'),
    });
  }

  private notify(error: { status?: number }, message: string): void {
    if ((error?.status || 0) >= 500 || error?.status === 0) {
      this.toastService.showError(message);
      return;
    }
    this.toastService.showWarn(message);
  }
}
